import {action, computed, observable} from 'mobx';
import { RouterStore } from 'mobx-react-router';
import { matchPath } from 'react-router';
import { includes, some } from 'lodash/collection';

import { ShortlistStore } from '../../modules/shortlist';
import Calendar, { DateList } from './calendar';
import Event from './event';
import FilterList from './filters';


export default class EventStore {
  title = null;
  events = [];
  pageCount = 12;

  @observable page = 1;

  constructor(data) {
    this.title = data.title;
    this.hiddenSlugs = data.hidden_slugs;
    this.extraTileChoices = data.extra_tiles;
    this.calendar = new Calendar(data.calendar);
    this.filters = new FilterList(data.filters);
    this.routingStore = new RouterStore();
    this.shortlistStore = new ShortlistStore();
    this.loadEvents(data.events);

    this.eventsFilteredByDate = this.eventsFilteredByDate.bind(this);
  }

  getMatch(path) {
    return matchPath(this.routingStore.location.pathname, {
      path,
      exact: true,
    });
  }

  getFilterFromLocation() {
    let matches = this.getMatch('/:filter/:slug/');

    if (!matches) {
      matches = this.getMatch('/:filter/:slug/date/:month/:day/');
    }

    if (matches) {
      const { filter, slug } = matches.params;
      return this.filters.getFilterByTypeAndSlug(filter, slug);
    }
    return null;
  }

  getDateFromLocation() {
    let matches = this.getMatch('/date/:month/:day/');

    if (!matches) {
      matches = this.getMatch('/:filter/:slug/date/:month/:day/');
    }

    if (matches) {
      const { month, day } = matches.params;
      return this.calendar.getByMonthAndDate(month, day);
    }
    return null;
  }

  filterPath(filter) {
    return `/${ filter.type }/${ filter.slug }`;
  }

  datePath(date) {
    return `/date/${ date.month + 1 }/${ date.date }`;
  }

  @computed
  get eventCount() {
    return this.page * this.pageCount;
  }

  @computed
  get hasMoreEvents() {
    return this.events.length > this.eventCount;
  }

  @computed
  get currentFilter() {
    return this.getFilterFromLocation();
  }

  @computed
  get currentDate() {
    return this.getDateFromLocation();
  }

  @action
  setDate = (date) => {
    const { push } = this.routingStore;

    if (this.currentFilter) {
      push(this.filterPath(this.currentFilter) + this.datePath(date));
    } else {
      push(this.datePath(date));
    }

    this.resetPage();
  };

  @action
  setFilter = (filter) => {
    const { push } = this.routingStore;

    if (this.currentDate) {
      push(this.filterPath(filter) + this.datePath(this.currentDate));
    } else {
      push(this.filterPath(filter));
    }

    this.resetPage();
  };

  @action
  unsetDate = () => {
    const { push } = this.routingStore;

    if (this.currentFilter) {
      push(this.filterPath(this.currentFilter));
    } else {
      push('/');
    }

    this.resetPage();
  };

  @action
  unsetFilter = () => {
    const { push } = this.routingStore;

    if (this.currentDate) {
      push(this.datePath(this.currentDate));
    } else {
      push('/');
    }

    this.resetPage();
  };

  @action
  resetPage = () => {
    this.page = 1;
  };

  @action
  nextPage = () => {
    this.page = this.page + 1;
  };

  loadEvents(events) {
    events.forEach(event => {
      this.events.push(new Event(event));
    });
  }

  @computed
  get filteredEvents() {
    let filteredEvents = this.eventsFilteredByFilter;
    filteredEvents = this.eventsFilteredByExclusions({ events: filteredEvents });
    filteredEvents = this.eventsFilteredByDate({ events: filteredEvents });
    return filteredEvents;
  }

  @computed
  get filteredEventIds() {
    return this.filteredEvents.map(event => event.id);
  }

  @computed
  get availableExtraTiles() {
    return this.extraTileChoices.filter(tile => this.filteredEventIds.includes(tile.id));
  }

  @computed
  get numExtraTiles() {
    const { length } = this.filteredEvents;
    const extraTilesStart = 8;
    if (length > extraTilesStart) {
      const maxNum = 4;
      return maxNum - (length % maxNum);
    }
    return 0;
  }

  @computed
  get extraTiles() {
    // TODO: Extra tiles are coming from the backend, but this function could be cleaner
    const { length } = this.filteredEvents;
    const tiles = this.availableExtraTiles;
    const num = Math.min(this.numExtraTiles, tiles.length);
    const minPosition = 3;
    const arr = [];
    for (let i = 0; i < num; i++) {
      let position = Math.round(Math.random() * (length - minPosition)) + minPosition;
      if (i === 0) {
        position = minPosition;
      } else if (i === num - 1) {
        position = length + num - 1;
      }
      const tile = tiles[Math.floor(Math.random() * tiles.length)];
      arr[i] = {
        position,
        content: tile.provocation,
        url: tile.url,
      };
    }
    return arr;
  }

  @computed
  get eventsFilteredByFilter() {
    let filteredEvents = this.events;
    if (this.currentFilter) {
      filteredEvents = filteredEvents.filter(this.currentFilter.filter);
    }
    return filteredEvents;
  }

  eventsFilteredByExclusions({ events } = {}) {
    let filteredEvents = events || this.events;
    if (!this.currentFilter || (this.currentFilter && !this.hiddenSlugs.includes(this.currentFilter.slug))) {
      filteredEvents = filteredEvents.filter(event => {
        return !some(this.hiddenSlugs, hiddenSlug => includes(event.tagSlugs, hiddenSlug));
      });
    }
    return filteredEvents;
  }

  eventsFilteredByDate({ events } = {}) {
    let filteredEvents = events || this.events;
    if (this.currentDate) {
      filteredEvents = filteredEvents.filter(event => event.dates.includes(this.currentDate));
    }
    return filteredEvents;
  }

  @computed
  get activeDates() {
    const filteredEvents = this.eventsFilteredByFilter;
    let activeDates = new Set();
    filteredEvents.forEach(event => {
      if (event.dates) {
        activeDates = new Set([...activeDates, ...event.dates.raw]);
      }
    });
    return new DateList(Array.from(activeDates));
  }

  @computed
  get activeFilters() {
    const filteredEvents = this.eventsFilteredByDate();

    let activeTags = new Set();
    let activeCollections = new Set();
    let activeCuratedCollections = new Set();

    filteredEvents.forEach(event => {
      if (event.tags) {
        activeTags = new Set([...activeTags, ...event.tagSlugs]);
      }
      if (event.collections) {
        activeCollections = new Set([...activeCollections, ...event.collectionSlugs]);
      }
      if (event.curatedCollections) {
        activeCuratedCollections = new Set([...activeCuratedCollections, ...event.curatedCollectionSlugs]);
      }
    });

    return {
      'tag': Array.from(activeTags),
      'collection': Array.from(activeCollections),
      'curator': Array.from(activeCuratedCollections),
    };
  }
}
